import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isMobileViewport } from '@/hooks/use-mobile';
import {
	removeTeamConversation,
	toggleCurrentConversationMute,
	toggleCurrentConversationStarred,
} from '../../../actions/teams';

/**
 * Phones: after leaving or deleting the open chat, take its screens (the
 * thread, and Info when it is open) off the stack and land on the list, so
 * Back and the iOS swipe can't return to a chat that is gone.
 * `onMobileBack` is the dashboard's thread → list back (it pops to the list
 * entry when that is the one underneath).
 */
export function closeRemovedThread({ navigate, onMobileBack, infoOpen, listPath }) {
	const backToList = () =>
		onMobileBack ? onMobileBack() : navigate(listPath, { replace: true });
	const idx = window.history.state?.idx ?? 0;

	if (infoOpen && idx > 0) {
		// Pop Info first: the list check reads the thread's history entry.
		window.addEventListener('popstate', () => setTimeout(backToList, 0), {
			once: true,
		});
		navigate(-1);
		return;
	}

	backToList();
}

/**
 * Star, mute and leave for one team conversation, read from the store so the
 * phone list's swipe actions, its long-press sheet and the Info page agree.
 * The desktop row menu (ConversationMenu) keeps its own copy of these.
 */
export default function useTeamConversationActions(
	conversation,
	{ isDirectMessage, onMobileBack } = {}
) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { _teamConversation: openConversationId } = useParams();
	const userId = useSelector((state) => state.auth.user?._id);

	const isMuted = Boolean(conversation?._mutedUsers?.includes(userId));
	const isStarred = Boolean(conversation?._starredUsers?.includes(userId));
	// channels follow the location and departments, so they can't be left
	const canLeave = Boolean(conversation) && !isDirectMessage && !conversation.channel;

	const toggleMute = () =>
		dispatch(
			toggleCurrentConversationMute({
				_teamConversation: conversation._id,
				_business: conversation._business,
				isCurrentConversationMuted: !isMuted,
			}),
		);

	const toggleStar = () =>
		dispatch(
			toggleCurrentConversationStarred({
				_teamConversation: conversation._id,
				_business: conversation._business,
				isCurrentConversationStarred: !isStarred,
			}),
		);

	const leave = async () => {
		const result = await dispatch(removeTeamConversation({ conversation }));

		// Leaving the open thread goes back to the list instead of a dead thread.
		if (!result?.error && openConversationId === conversation._id) {
			if (isMobileViewport()) {
				closeRemovedThread({
					navigate,
					onMobileBack,
					infoOpen: Boolean(location.state?.mobilePanel),
					listPath: '/groupchat/groups',
				});
			} else {
				navigate('/groupchat/groups', { replace: true });
			}
		}

		return result;
	};

	return { isMuted, isStarred, canLeave, toggleMute, toggleStar, leave };
}
